import React from 'react';
import type { CSSProperties, MouseEventHandler } from 'react';

/* ═══════════════════════════════════════════════════════════════
   DRPX Icon Component — Filled + Schatten Style
   119 SVG Icons, kategorisiert und farbkodiert.
   
   Usage:  <Icon name="package" />
           <Icon name="truck" size={24} />
           <Icon name="star" color="#custom" />
   ═══════════════════════════════════════════════════════════════ */

/** Einzelne Icon-Definition: Farbe + Render-Funktion. */
interface IconDef {
  c: string;
  r: (c: string) => React.ReactNode;
}

// Category colors — vereinfacht (vorher 14 Kategorien, 10 davon identisch #16a34a)
const C = {
  brand: '#16a34a', // Hauptfarbe: Aufträge, Transport, Dokumente, System, Erfolg
  amber: '#d97706', // Bewertungen, Sterne
  red: '#dc2626', // Warnung, Fehler
  emerald: '#059669', // Geld, Finanzen
  profile: '#64748b', // Nutzer, Profil
  orange: '#ea580c', // Media, Highlights
  slate: '#475569', // Neutral, UI, Zeit
};

// Helper: white stroke element
const W = function (d: string, o?: number, w?: number): React.JSX.Element {
  return (
    <path
      d={d}
      fill="none"
      stroke="#fff"
      strokeWidth={w || 1.2}
      opacity={o || 0.5}
      strokeLinecap="round"
      strokeLinejoin="round" />
  );
};
// Helper: white line
const WL = function (x1: number, y1: number, x2: number, y2: number, o?: number, w?: number): React.JSX.Element {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke="#fff"
      strokeWidth={w || 1.5}
      opacity={o || 0.6}
      strokeLinecap="round" />
  );
};
// Helper: white circle
const WC = function (cx: number, cy: number, r: number, o?: number): React.JSX.Element {
  return <circle cx={cx} cy={cy} r={r} fill="#fff" opacity={o || 1} />;
};

// All icon definitions: { color, r: render function(color) }
const ICONS: Record<string, IconDef> = {
  // ═══ TRANSPORT (Cyan) ═══
  'truck-heavy': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={1} y={6} width={15} height={13} rx={2} fill={c} />
          <path d="M16 10H20.5L23 14V19H16Z" fill={c} opacity={0.8} />
          {WL(3, 9, 12, 9, 0.3)}
          <circle cx={6} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
          <circle cx={19} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
        </React.Fragment>
      );
    },
  },
  truck: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={1} y={7} width={14} height={12} rx={2} fill={c} />
          <path d="M15 11H19.5L22 14.5V19H15Z" fill={c} opacity={0.8} />
          {WL(3, 10, 11, 10, 0.3)}
          <circle cx={5.5} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
          <circle cx={18.5} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
        </React.Fragment>
      );
    },
  },
  car: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M5 15L7 9H17L19 15H5Z" fill={c} />
          <rect x={2} y={15} width={20} height={5} rx={2} fill={c} opacity={0.85} />
          {W('M7 9L5 15M17 9L19 15', 0.4, 1)}
          <circle cx={6.5} cy={20} r={2} fill="#fff" stroke={c} strokeWidth={1.2} />
          <circle cx={17.5} cy={20} r={2} fill="#fff" stroke={c} strokeWidth={1.2} />
        </React.Fragment>
      );
    },
  },
  van: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 8H16V19H2Z" fill={c} />
          <path d="M16 11H20L22 15V19H16Z" fill={c} opacity={0.8} />
          <rect x={3} y={9} width={5} height={4} rx={1} fill="#fff" opacity={0.2} />
          <circle cx={6} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
          <circle cx={19} cy={19} r={2.5} fill="#fff" stroke={c} strokeWidth={1.5} />
        </React.Fragment>
      );
    },
  },
  bicycle: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={6} cy={17} r={4} fill={c} />
          <circle cx={18} cy={17} r={4} fill={c} />
          <circle cx={6} cy={17} r={2} fill="#fff" opacity={0.3} />
          <circle cx={18} cy={17} r={2} fill="#fff" opacity={0.3} />
          <path
            d="M6 17L10 7H14L18 17M10 7L12 4"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  motorcycle: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={5} cy={17} r={4} fill={c} />
          <circle cx={19} cy={17} r={4} fill={c} />
          <circle cx={5} cy={17} r={2} fill="#fff" opacity={0.3} />
          <circle cx={19} cy={17} r={2} fill="#fff" opacity={0.3} />
          <path
            d="M5 17L9 9H15L19 17M9 9L7 6H10"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  map: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M3 5L9 3V19L3 21V5Z" fill={c} opacity={0.7} />
          <path d="M9 3L15 5V21L9 19V3Z" fill={c} />
          <path d="M15 5L21 3V19L15 21V5Z" fill={c} opacity={0.7} />
          {W('M9 3V19M15 5V21', 0.4, 1)}
        </React.Fragment>
      );
    },
  },

  // ═══ AUFTRÄGE (Blue) ═══
  package: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M2 8L12 2L22 8V20L12 26L2 20Z"
            fill={c}
            transform="translate(0,-2) scale(0.92)" />
          <path d="M1.84 5.52L11.04 0.84L20.24 5.52V16.4L11.04 22.92L1.84 16.4Z" fill={c} />
          {W('M11 12.3L20.2 5.5M11 12.3L1.8 5.5M11 12.3V22.9', 0.45, 1)}
        </React.Fragment>
      );
    },
  },
  upload: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={12} width={18} height={9} rx={2} fill={c} />
          <path d="M12 3L7 8H10V14H14V8H17L12 3Z" fill={c} />
          {WL(7, 18, 11, 18, 0.5)}
          {WC(17, 18, 1.2, 0.5)}
        </React.Fragment>
      );
    },
  },
  'offer-incoming': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={2} fill={c} />
          {W('M2 8L12 14L22 8', 0.5, 1.2)}
          <rect x={2} y={4} width={20} height={4} rx={2} fill={c} opacity={0.9} />
        </React.Fragment>
      );
    },
  },
  handshake: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 11L7 7L12 10L17 7L22 11V16L17 19L12 16L7 19L2 16Z" fill={c} />
          {W('M7 7L12 10L17 7M7 19L12 16L17 19', 0.4, 1)}
        </React.Fragment>
      );
    },
  },
  'finish-flag': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={4} y={2} width={16} height={12} rx={1} fill={c} />
          <rect x={4} y={2} width={8} height={6} fill="#fff" opacity={0.25} />
          <rect x={12} y={8} width={8} height={6} fill="#fff" opacity={0.25} />
          <line
            x1={4}
            y1={2}
            x2={4}
            y2={22}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },

  // ═══ DOKUMENTE (Violet) ═══
  document: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M5 2H16L20 6V22H5Z" fill={c} />
          <path d="M16 2V6H20" fill={c} opacity={0.7} />
          {WL(8, 10, 16, 10)}
          {WL(8, 14, 16, 14)}
          {WL(8, 18, 13, 18)}
        </React.Fragment>
      );
    },
  },
  'document-edit': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M5 2H16L20 6V22H5Z" fill={c} />
          <path d="M16 2V6H20" fill={c} opacity={0.7} />
          {W('M8 18L14 12L17 15L11 21H8V18Z', 0.6, 1.2)}
        </React.Fragment>
      );
    },
  },
  clipboard: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={4} y={4} width={16} height={18} rx={2} fill={c} />
          <rect
            x={8}
            y={2}
            width={8}
            height={4}
            rx={1}
            fill={c}
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.9} />
          {WL(8, 11, 16, 11)}
          {WL(8, 14.5, 16, 14.5)}
          {WL(8, 18, 13, 18)}
        </React.Fragment>
      );
    },
  },
  scroll: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M6 3H18C19.1 3 20 3.9 20 5V19C20 20.1 19.1 21 18 21H6C4.9 21 4 20.1 4 19V5C4 3.9 4.9 3 6 3Z"
            fill={c} />
          <path
            d="M4 5C4 3.9 4.9 3 6 3H8V21H6C4.9 21 4 20.1 4 19V5Z"
            fill={c}
            opacity={0.75} />
          {WL(10, 8, 17, 8)}
          {WL(10, 12, 17, 12)}
          {WL(10, 16, 14, 16)}
        </React.Fragment>
      );
    },
  },
  folder: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 6H10L12 4H22V20H2V6Z" fill={c} />
          <path d="M2 8H22V20H2Z" fill={c} opacity={0.85} />
          {WL(2, 8, 22, 8, 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  notebook: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={4} y={2} width={16} height={20} rx={2} fill={c} />
          <line x1={9} y1={2} x2={9} y2={22} stroke="#fff" strokeWidth={1} opacity={0.3} />
          {WL(12, 7, 17, 7)}
          {WL(12, 11, 17, 11)}
          {WL(12, 15, 15, 15)}
        </React.Fragment>
      );
    },
  },
  'folder-tabs': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={6} width={20} height={14} rx={2} fill={c} />
          <path d="M5 6V4H9V6" fill={c} opacity={0.7} />
          <path d="M11 6V4H15V6" fill={c} opacity={0.7} />
          {WL(6, 11, 18, 11, 0.3)}
          {WL(6, 15, 15, 15, 0.3)}
        </React.Fragment>
      );
    },
  },

  // ═══ STATUS ═══
  'check-circle': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={11} fill={c} />
          <circle
            cx={12}
            cy={12}
            r={8.5}
            fill="none"
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.2} />
          <path
            d="M7 12L10.5 16L17.5 8"
            fill="none"
            stroke="#fff"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  check: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M4 12L9.5 18L20 6"
            fill="none"
            stroke={c}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  'cross-circle': {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={11} fill={c} />
          <path
            d="M8 8L16 16M16 8L8 16"
            fill="none"
            stroke="#fff"
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  cross: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M6 6L18 18M18 6L6 18"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  // close: alias → set after ICONS definition (ICONS.close = ICONS.cross)
  info: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={11} fill={c} />
          <circle cx={12} cy={12} r={9.5} fill={c} opacity={0.85} />
          <line
            x1={12}
            y1={11}
            x2={12}
            y2={17}
            stroke="#fff"
            strokeWidth={2.2}
            strokeLinecap="round" />
          {WC(12, 7.5, 1.5)}
        </React.Fragment>
      );
    },
  },
  warning: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M12 2L1 21H23Z" fill={c} />
          <path d="M12 5.5L4 19H20Z" fill={c} opacity={0.85} />
          <line
            x1={12}
            y1={10}
            x2={12}
            y2={15}
            stroke="#fff"
            strokeWidth={2.2}
            strokeLinecap="round" />
          {WC(12, 18, 1.5)}
        </React.Fragment>
      );
    },
  },
  blocked: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <line
            x1={5}
            y1={5}
            x2={19}
            y2={19}
            stroke="#fff"
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  forbidden: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill="none" stroke={c} strokeWidth={3} />
          <line
            x1={4}
            y1={4}
            x2={20}
            y2={20}
            stroke={c}
            strokeWidth={3}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  hourglass: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M6 2H18V7L12 12L6 7V2Z" fill={c} />
          <path d="M6 22H18V17L12 12L6 17V22Z" fill={c} opacity={0.7} />
          <rect x={6} y={2} width={12} height={2} rx={1} fill={c} />
          <rect x={6} y={20} width={12} height={2} rx={1} fill={c} />
          {W('M9 4V6.5L12 9.5M15 4V6.5L12 9.5', 0.3, 1)}
        </React.Fragment>
      );
    },
  },

  // ═══ BEWERTUNGEN (Amber) ═══
  star: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2L15.1 8.3H22L16.5 13L18.8 20L12 15.8L5.2 20L7.5 13L2 8.3H8.9Z"
            fill={c} />
          <path
            d="M12 4.5L14 8.3H18L15 11.5L16 16L12 13.5L8 16L9 11.5L6 8.3H10Z"
            fill={c}
            opacity={0.6} />
        </React.Fragment>
      );
    },
  },
  'star-empty': {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2L15.1 8.3H22L16.5 13L18.8 20L12 15.8L5.2 20L7.5 13L2 8.3H8.9Z"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  trophy: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M6 3H18V10C18 13.3 15.3 16 12 16C8.7 16 6 13.3 6 10V3Z" fill={c} />
          <path d="M6 5H3V8C3 9.7 4.3 11 6 11V5Z" fill={c} opacity={0.7} />
          <path d="M18 5H21V8C21 9.7 19.7 11 18 11V5Z" fill={c} opacity={0.7} />
          <rect x={10} y={16} width={4} height={3} fill={c} />
          <rect x={7} y={19} width={10} height={2} rx={1} fill={c} />
          <rect x={8} y={4} width={4} height={3} rx={0.5} fill="#fff" opacity={0.2} />
        </React.Fragment>
      );
    },
  },
  diamond: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M12 2L22 12L12 22L2 12Z" fill={c} />
          <path d="M12 2L17 7H7Z" fill="#fff" opacity={0.2} />
          {W('M7 7H17L12 2M2 12L7 7M22 12L17 7', 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  sparkle: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M12 2L14 9L21 12L14 15L12 22L10 15L3 12L10 9Z" fill={c} />
          <path d="M12 5L13 9L17 12L13 15L12 19L11 15L7 12L11 9Z" fill={c} opacity={0.6} />
        </React.Fragment>
      );
    },
  },

  // ═══ FINANZEN (Emerald) ═══
  'money-bag': {
    c: C.emerald,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M8 4L12 2L16 4L18 8C20 12 20 16 18 20H6C4 16 4 12 6 8L8 4Z" fill={c} />
          <path d="M10 3.5L12 2.5L14 3.5" fill="none" stroke={c} strokeWidth={2} />
          <text
            x={12}
            y={14}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#fff"
            style={{ fontSize: '10px', fontWeight: 800, fontFamily: "'DM Sans',sans-serif" }}>
            €
          </text>
        </React.Fragment>
      );
    },
  },
  'money-fly': {
    c: C.emerald,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={5} width={16} height={12} rx={2} fill={c} />
          <circle cx={10} cy={11} r={3} fill="#fff" opacity={0.3} />
          <text
            x={10}
            y={11.5}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#fff"
            style={{ fontSize: '8px', fontWeight: 800, fontFamily: "'DM Sans',sans-serif" }}>
            €
          </text>
          <path
            d="M18 4L22 2V8L18 10M18 12L22 10V16L18 18"
            fill="none"
            stroke={c}
            strokeWidth={1.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  euro: {
    c: C.emerald,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={11} fill={c} />
          <circle
            cx={12}
            cy={12}
            r={8.5}
            fill="none"
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.2} />
          <text
            x={12}
            y={12.5}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#fff"
            style={{ fontSize: '14px', fontWeight: 800, fontFamily: "'DM Sans',sans-serif" }}>
            €
          </text>
        </React.Fragment>
      );
    },
  },
  receipt: {
    c: C.emerald,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M5 2L7 4L9 2L11 4L13 2L15 4L17 2L19 4V22L17 20L15 22L13 20L11 22L9 20L7 22L5 20V2Z"
            fill={c} />
          {WL(8, 8, 16, 8)}
          {WL(8, 11.5, 16, 11.5)}
          {WL(8, 15, 13, 15)}
        </React.Fragment>
      );
    },
  },
  bank: {
    c: C.emerald,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 9L12 3L22 9V11H2V9Z" fill={c} />
          <rect x={2} y={19} width={20} height={2} rx={0.5} fill={c} />
          <rect x={4} y={11} width={3} height={8} fill={c} opacity={0.8} />
          <rect x={10.5} y={11} width={3} height={8} fill={c} opacity={0.8} />
          <rect x={17} y={11} width={3} height={8} fill={c} opacity={0.8} />
          {WC(12, 7, 1.2, 0.35)}
        </React.Fragment>
      );
    },
  },

  // ═══ NUTZER (Pink) ═══
  user: {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={8} r={5} fill={c} />
          <path d="M3 22C3 16.5 7 13 12 13C17 13 21 16.5 21 22" fill={c} opacity={0.85} />
          <circle cx={10.5} cy={6.5} r={1.8} fill="#fff" opacity={0.2} />
        </React.Fragment>
      );
    },
  },
  users: {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={9} cy={8} r={4.5} fill={c} />
          <path d="M1 22C1 17 4.5 14 9 14C13.5 14 17 17 17 22" fill={c} opacity={0.85} />
          <circle cx={17} cy={8} r={3.5} fill={c} opacity={0.7} />
          <path
            d="M17 14C20 14.5 22 17 22 22"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            opacity={0.7} />
        </React.Fragment>
      );
    },
  },
  wave: {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M7 2C7 2 4 7 4 10C4 12 5.5 13.5 7.5 13.5C8.5 13.5 9 13 9 12V6C9 4 10 3 11 3C12 3 13 4 13 6V11C13 12 14 13 15 13C16 13 16.5 12 16.5 11V8C16.5 7 17 6 18 6C19 6 20 7 20 9V15C20 19 17 22 13 22H11C7 22 4 19 4 15"
            fill={c} />
          {W('M9 6V5C9 3.5 10.5 3 11 3M13 6V11', 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  eye: {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M1 12C3 7 7 4 12 4C17 4 21 7 23 12C21 17 17 20 12 20C7 20 3 17 1 12Z"
            fill={c} />
          <circle cx={12} cy={12} r={4} fill="#fff" opacity={0.9} />
          <circle cx={12} cy={12} r={2} fill={c} />
        </React.Fragment>
      );
    },
  },
  heart: {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 21L3.6 12.6C1.5 10.5 1.5 7 3.6 5C5.7 3 9.2 3 11.3 5L12 5.7L12.7 5C14.8 3 18.3 3 20.4 5C22.5 7 22.5 10.5 20.4 12.6L12 21Z"
            fill={c} />
          <path
            d="M7 6.5C5.5 6.5 4.5 7.5 4.5 9"
            fill="none"
            stroke="#fff"
            strokeWidth={1.2}
            opacity={0.35}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },

  // ═══ KOMMUNIKATION (Indigo) ═══
  bell: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2C8.7 2 6 4.7 6 8V12L4 15V16H20V15L18 12V8C18 4.7 15.3 2 12 2Z"
            fill={c} />
          <path d="M9 17C9 18.7 10.3 20 12 20C13.7 20 15 18.7 15 17" fill={c} />
          {W('M6 8V12L4 15H20L18 12V8', 0.4, 1)}
        </React.Fragment>
      );
    },
  },
  mail: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={2} fill={c} />
          {W('M2 6L12 13L22 6', 0.55, 1.3)}
        </React.Fragment>
      );
    },
  },
  'bell-off': {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2C8.7 2 6 4.7 6 8V12L4 15V16H20V15L18 12V8C18 4.7 15.3 2 12 2Z"
            fill={c}
            opacity={0.4} />
          <path
            d="M9 17C9 18.7 10.3 20 12 20C13.7 20 15 18.7 15 17"
            fill={c}
            opacity={0.4} />
          {W('M3 3L21 21', 1, 1.5)}
        </React.Fragment>
      );
    },
  },
  email: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={2} fill={c} />
          {W('M2 6L12 13L22 6', 0.55, 1.3)}
        </React.Fragment>
      );
    },
  },
  envelope: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={2} fill={c} />
          {W('M2 6L12 13L22 6', 0.55, 1.3)}
          <rect x={2} y={4} width={20} height={3} rx={2} fill={c} />
        </React.Fragment>
      );
    },
  },
  phone: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M5 3C5 3 3 5 3 7C3 13 7 18 13 21C15 21 17 19 17 19L15 14L12 15.5C10.5 14.5 9 12.5 8.5 11L10 8.5L5 3Z"
            fill={c} />
          <path
            d="M13 3C17 3 21 7 21 11"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <path
            d="M13 7C15 7 17 9 17 11"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  mailbox: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 10C4 7 6 5 9 5H20V18H4V10Z" fill={c} />
          <rect x={14} y={2} width={3} height={5} rx={0.5} fill={c} opacity={0.7} />
          {WL(8, 10, 12, 10)}
          {WL(8, 13, 11, 13)}
          <path d="M4 10C4 7 6 5 9 5" fill="none" stroke={c} strokeWidth={3} />
          <line x1={8} y1={18} x2={8} y2={22} stroke={c} strokeWidth={2} />
          <line x1={16} y1={18} x2={16} y2={22} stroke={c} strokeWidth={2} />
        </React.Fragment>
      );
    },
  },
  postbox: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={4} y={3} width={16} height={15} rx={3} fill={c} />
          <rect x={8} y={8} width={8} height={3} rx={1} fill="#fff" opacity={0.3} />
          <line x1={8} y1={18} x2={8} y2={22} stroke={c} strokeWidth={2} />
          <line x1={16} y1={18} x2={16} y2={22} stroke={c} strokeWidth={2} />
          <rect x={4} y={3} width={16} height={4} rx={3} fill={c} opacity={0.9} />
        </React.Fragment>
      );
    },
  },
  fax: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={7} width={18} height={14} rx={2} fill={c} />
          <rect x={7} y={3} width={10} height={6} rx={1} fill={c} opacity={0.7} />
          <rect x={6} y={12} width={6} height={5} rx={1} fill="#fff" opacity={0.25} />
          {WC(16, 14, 1.2, 0.3)}
          {WC(16, 17, 1.2, 0.3)}
        </React.Fragment>
      );
    },
  },

  message: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M3 6C3 4.9 3.9 4 5 4H19C20.1 4 21 4.9 21 6V14C21 15.1 20.1 16 19 16H9L5 20V16C3.9 16 3 15.1 3 14V6Z"
            fill={c} />
          {W('M7 9H17', 0.5, 1.3)}
          {W('M7 12H13', 0.5, 1.3)}
        </React.Fragment>
      );
    },
  },
  send: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M22 2L11 13"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <path d="M22 2L15 22L11 13L2 9L22 2Z" fill={c} opacity={0.9} />
        </React.Fragment>
      );
    },
  },

  // ═══ SYSTEM/UI (Slate) ═══
  refresh: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M4 12C4 7.6 7.6 4 12 4C15 4 17.5 5.5 19 8"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <path
            d="M20 12C20 16.4 16.4 20 12 20C9 20 6.5 18.5 5 16"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <path
            d="M15 8H20V3"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <path
            d="M9 16H4V21"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  lock: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={4} y={10} width={16} height={12} rx={2} fill={c} />
          <path
            d="M8 10V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V10"
            fill="none"
            stroke={c}
            strokeWidth={2.5} />
          {WC(12, 16, 2, 0.35)}
        </React.Fragment>
      );
    },
  },
  search: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={10.5} cy={10.5} r={7} fill={c} opacity={0.85} />
          <circle cx={10.5} cy={10.5} r={4.5} fill="#fff" opacity={0.3} />
          <line
            x1={16}
            y1={16}
            x2={21}
            y2={21}
            stroke={c}
            strokeWidth={3}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  settings: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={5} fill={c} />
          <circle cx={12} cy={12} r={3} fill={c} opacity={0.6} />
          <path
            d="M12 1V4M12 20V23M1 12H4M20 12H23M4.2 4.2L6.3 6.3M17.7 17.7L19.8 19.8M19.8 4.2L17.7 6.3M6.3 17.7L4.2 19.8"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  wrench: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M16 2C13.2 2 11 4.2 11 7C11 7.7 11.1 8.4 11.4 9L3 17.4C2.2 18.2 2.2 19.5 3 20.3L3.7 21C4.5 21.8 5.8 21.8 6.6 21L15 12.6C15.6 12.9 16.3 13 17 13C19.8 13 22 10.8 22 8C22 7.4 21.9 6.8 21.7 6.3L18.5 9.5L15 8.5L14 5L17.2 1.8C16.8 2 16.4 2 16 2Z"
            fill={c} />
          {W('M18.5 9.5L15 8.5L14 5L17.2 1.8', 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  link: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M10 14L8.5 15.5C6.8 17.2 4 17.2 2.3 15.5C0.6 13.8 0.6 11 2.3 9.3L6 5.5C7.7 3.8 10.5 3.8 12.2 5.5C12.9 6.2 13.3 7 13.4 7.9"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <path
            d="M14 10L15.5 8.5C17.2 6.8 20 6.8 21.7 8.5C23.4 10.2 23.4 13 21.7 14.7L18 18.5C16.3 20.2 13.5 20.2 11.8 18.5C11.1 17.8 10.7 17 10.6 16.1"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  'chevron-down': {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M6 9L12 15L18 9"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  menu: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <line
            x1={3}
            y1={6}
            x2={21}
            y2={6}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <line
            x1={3}
            y1={12}
            x2={21}
            y2={12}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <line
            x1={3}
            y1={18}
            x2={21}
            y2={18}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  save: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 2H16L20 6V20C20 21.1 19.1 22 18 22H4C4 22 4 2 4 2Z" fill={c} />
          <rect x={7} y={2} width={8} height={6} rx={1} fill="#fff" opacity={0.25} />
          <rect x={7} y={14} width={10} height={6} rx={1} fill="#fff" opacity={0.25} />
          <rect x={12} y={3} width={2} height={4} rx={0.5} fill={c} opacity={0.5} />
        </React.Fragment>
      );
    },
  },
  trash: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M5 6H19L18 21H6L5 6Z" fill={c} />
          <rect x={3} y={4} width={18} height={2} rx={1} fill={c} />
          <path
            d="M9 4V2.5C9 2.2 9.2 2 9.5 2H14.5C14.8 2 15 2.2 15 2.5V4"
            fill="none"
            stroke={c}
            strokeWidth={1.5} />
          {WL(9, 9, 9, 18, 0.3, 1.2)}
          {WL(12, 9, 12, 18, 0.3, 1.2)}
          {WL(15, 9, 15, 18, 0.3, 1.2)}
        </React.Fragment>
      );
    },
  },
  key: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={8} cy={15} r={5} fill={c} />
          <circle cx={8} cy={15} r={2.5} fill="#fff" opacity={0.25} />
          <path
            d="M12 11L20 3M18 3H21V6M17 7L19.5 4.5"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },

  // ═══ MEDIA/FOTOS (Orange) ═══
  camera: {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 8H22V20H2Z" fill={c} />
          <path d="M8 8L10 5H14L16 8" fill={c} opacity={0.8} />
          <circle cx={12} cy={14} r={4} fill="#fff" opacity={0.3} />
          <circle cx={12} cy={14} r={2} fill={c} opacity={0.5} />
        </React.Fragment>
      );
    },
  },
  'camera-alt': {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={7} width={20} height={13} rx={2} fill={c} />
          <path d="M9 7L10.5 4.5H13.5L15 7" fill={c} />
          <circle cx={12} cy={14} r={3.5} fill="#fff" opacity={0.3} />
          <circle cx={12} cy={14} r={1.5} fill={c} />
        </React.Fragment>
      );
    },
  },
  'photo-saved': {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={3} width={18} height={18} rx={2} fill={c} />
          <circle cx={8.5} cy={8.5} r={2.5} fill="#fff" opacity={0.35} />
          <path
            d="M3 17L8 12L12 16L15 13L21 19V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V17Z"
            fill="#fff"
            opacity={0.25} />
        </React.Fragment>
      );
    },
  },
  fire: {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2C12 2 7 8 7 13C7 17 9 21 12 22C15 21 17 17 17 13C17 8 12 2 12 2Z"
            fill={c} />
          <path
            d="M12 10C12 10 10 13 10 15.5C10 17.5 11 19 12 19.5C13 19 14 17.5 14 15.5C14 13 12 10 12 10Z"
            fill="#fff"
            opacity={0.3} />
        </React.Fragment>
      );
    },
  },
  rocket: {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2C12 2 7 5 7 12C7 16 9 19 12 22C15 19 17 16 17 12C17 5 12 2 12 2Z"
            fill={c} />
          <circle cx={12} cy={11} r={2} fill="#fff" opacity={0.35} />
          <path d="M7 15L4 17L5 20L7 18M17 15L20 17L19 20L17 18" fill={c} opacity={0.7} />
        </React.Fragment>
      );
    },
  },

  // ═══ ZEIT (Slate) ═══
  clock: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <circle
            cx={12}
            cy={12}
            r={7.5}
            fill="none"
            stroke="#fff"
            strokeWidth={0.6}
            opacity={0.2} />
          <path
            d="M12 6V12L16 14"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  calendar: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={4} width={18} height={18} rx={2} fill={c} />
          <rect x={3} y={4} width={18} height={5} rx={2} fill={c} opacity={0.9} />
          <line
            x1={7}
            y1={2}
            x2={7}
            y2={6}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={17}
            y1={2}
            x2={17}
            y2={6}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          {WL(3, 9, 21, 9, 0.3, 1)}
          {WC(8, 13, 1.3, 0.35)}
          {WC(12, 13, 1.3, 0.35)}
          {WC(16, 13, 1.3, 0.35)}
          {WC(8, 17, 1.3, 0.35)}
          {WC(12, 17, 1.3, 0.35)}
        </React.Fragment>
      );
    },
  },
  stopwatch: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={13} r={9} fill={c} />
          <rect x={10.5} y={1} width={3} height={3} rx={1} fill={c} />
          <path
            d="M12 8V13L15 16"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <path
            d="M19 6L20.5 4.5"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  sunrise: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 18C4 14 7.6 11 12 11C16.4 11 20 14 20 18" fill={c} />
          <line
            x1={12}
            y1={3}
            x2={12}
            y2={7}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={4}
            y1={7}
            x2={6.5}
            y2={9.5}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={20}
            y1={7}
            x2={17.5}
            y2={9.5}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={2}
            y1={18}
            x2={22}
            y2={18}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  'sun-cloud': {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={10} cy={8} r={4} fill={c} />
          <path
            d="M3 8H5M15 8H17M5 3L7 5M13 3L11 5M10 1V3"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <path
            d="M7 18C4.8 18 3 16.2 3 14C3 12 4.5 10.3 6.5 10C7.3 8.3 9 7 11 7C13.5 7 15.5 8.8 16 11C18.2 11.2 20 13 20 15.5C20 17.5 18.5 18 17 18H7Z"
            fill={C.slate}
            opacity={0.85} />
        </React.Fragment>
      );
    },
  },
  sunset: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 16C4 12 7.6 9 12 9C16.4 9 20 12 20 16" fill={c} />
          <line
            x1={12}
            y1={2}
            x2={12}
            y2={5}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={4}
            y1={5}
            x2={6.5}
            y2={7.5}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={20}
            y1={5}
            x2={17.5}
            y2={7.5}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={1}
            y1={20}
            x2={23}
            y2={20}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <line
            x1={2}
            y1={16}
            x2={22}
            y2={16}
            stroke={c}
            strokeWidth={1.5}
            strokeLinecap="round"
            opacity={0.5} />
        </React.Fragment>
      );
    },
  },
  sun: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={5} fill={c} />
          <circle cx={10.5} cy={10.5} r={2} fill="#fff" opacity={0.2} />
          <path
            d="M12 2V5M12 19V22M2 12H5M19 12H22M4.9 4.9L7.1 7.1M16.9 16.9L19.1 19.1M19.1 4.9L16.9 7.1M7.1 16.9L4.9 19.1"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  // Darstellung „Auto" (wie Gerät): halb hell, halb dunkel — 10.10.2026
  'theme-auto': {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={9} fill="none" stroke={c} strokeWidth={2} />
          <path d="M12 3A9 9 0 0 0 12 21Z" fill={c} />
        </React.Fragment>
      );
    },
  },
  moon: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M21 12.8C20 17.5 16 21 11 21C5.5 21 1 16.5 1 11C1 6 4.5 2 9.2 1C7.8 3 7 5.5 7 8C7 13 11 17 16 17C18.5 17 20 16 21 12.8Z"
            fill={c} />
          <path
            d="M14 5C14 5 15 6 16 6C17 6 18 5 18 5C18 5 17 6 17 7C17 8 18 9 18 9C18 9 17 8 16 8C15 8 14 9 14 9C14 9 15 8 15 7C15 6 14 5 14 5Z"
            fill={c}
            opacity={0.5} />
        </React.Fragment>
      );
    },
  },

  // ═══ ORTE/GEBÄUDE (Blue) ═══
  // Firma: Hochhaus + Anbau (Wunsch Sertan 21.10.2026, Vorschlag A). Vorher
  // ein einzelnes Rechteck mit 25 %-Fenstern — in klein nur ein grüner Block.
  building: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={3} width={11} height={19} rx={1} fill={c} />
          <rect x={14} y={9} width={7} height={13} rx={1} fill={c} opacity={0.7} />
          <rect x={5} y={6} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={9} y={6} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={5} y={10} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={9} y={10} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={5} y={14} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={9} y={14} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={16.5} y={12} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={16.5} y={16} width={2.2} height={2.2} rx={0.4} fill="#fff" opacity={0.9} />
          <rect x={6.8} y={18} width={3.4} height={4} rx={0.5} fill="#fff" opacity={0.9} />
        </React.Fragment>
      );
    },
  },
  home: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M3 12L12 4L21 12V21H3V12Z" fill={c} />
          <path
            d="M3 12L12 4L21 12"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <rect x={9} y={14} width={6} height={8} rx={1} fill="#fff" opacity={0.25} />
        </React.Fragment>
      );
    },
  },
  pin: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2C7.6 2 4 5.6 4 10C4 16 12 22 12 22C12 22 20 16 20 10C20 5.6 16.4 2 12 2Z"
            fill={c} />
          <circle cx={12} cy={10} r={3} fill="#fff" opacity={0.35} />
        </React.Fragment>
      );
    },
  },
  factory: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 22V10L8 14V10L14 14V10L20 14V4H22V22H2Z" fill={c} />
          <rect x={20} y={2} width={2} height={5} fill={c} opacity={0.8} />
          {W('M8 14V10L2 10M14 14V10L8 10', 0.25, 1)}
        </React.Fragment>
      );
    },
  },
  hospital: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={4} width={18} height={18} rx={2} fill={c} />
          <path
            d="M12 8V16M8 12H16"
            fill="none"
            stroke="#fff"
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  school: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M2 10L12 5L22 10V22H2V10Z" fill={c} />
          <path d="M2 10L12 5L22 10" fill="none" stroke={c} strokeWidth={2} />
          <rect x={9} y={14} width={6} height={8} rx={1} fill="#fff" opacity={0.25} />
          {WC(12, 11, 1.5, 0.25)}
        </React.Fragment>
      );
    },
  },
  hotel: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={2} width={18} height={20} rx={2} fill={c} />
          {WC(8, 7, 1, 0.25)}
          {WC(12, 7, 1, 0.25)}
          {WC(16, 7, 1, 0.25)}
          {WC(8, 11, 1, 0.25)}
          {WC(16, 11, 1, 0.25)}
          <rect x={10} y={15} width={4} height={7} rx={1} fill="#fff" opacity={0.25} />
          <text
            x={12}
            y={11.5}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#fff"
            opacity={0.4}
            style={{ fontSize: '5px', fontWeight: 700 }}>
            H
          </text>
        </React.Fragment>
      );
    },
  },
  globe: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <ellipse
            cx={12}
            cy={12}
            rx={4}
            ry={10}
            fill="none"
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.3} />
          {WL(2, 9, 22, 9, 0.25, 1)}
          {WL(2, 15, 22, 15, 0.25, 1)}
        </React.Fragment>
      );
    },
  },
  'globe-web': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <ellipse
            cx={12}
            cy={12}
            rx={4}
            ry={10}
            fill="none"
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.3} />
          {WL(2, 8, 22, 8, 0.2, 1)}
          {WL(2, 16, 22, 16, 0.2, 1)}
          <line
            x1={12}
            y1={2}
            x2={12}
            y2={22}
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.2} />
        </React.Fragment>
      );
    },
  },

  // ═══ ZERTIFIKATE/SICHERHEIT (Indigo) ═══
  'id-card': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={2} fill={c} />
          <rect x={2} y={4} width={20} height={4} rx={2} fill={c} opacity={0.9} />
          <circle cx={8} cy={14} r={2.5} fill="#fff" opacity={0.3} />
          {WL(13, 12, 19, 12, 0.4)}
          {WL(13, 15, 17, 15, 0.4)}
          {WL(13, 18, 19, 18, 0.3)}
        </React.Fragment>
      );
    },
  },
  shield: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M12 2L3 6V12C3 17 7 21 12 22C17 21 21 17 21 12V6L12 2Z" fill={c} />
          <path
            d="M12 4L5 7.5V12C5 16 8 19.5 12 20.5C16 19.5 19 16 19 12V7.5L12 4Z"
            fill={c}
            opacity={0.7} />
          <path
            d="M8.5 12L11 14.5L16 9.5"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  customs: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={2} width={18} height={20} rx={2} fill={c} />
          <circle cx={12} cy={10} r={4} fill="#fff" opacity={0.25} />
          {WL(12, 8, 12, 12, 0.5, 1.5)}
          {WL(10, 10, 14, 10, 0.5, 1.5)}
          {WL(7, 17, 17, 17, 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  helmet: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 14C4 9 7.6 5 12 5C16.4 5 20 9 20 14H4Z" fill={c} />
          <rect x={2} y={14} width={20} height={3} rx={1.5} fill={c} opacity={0.85} />
          <path
            d="M7 8C8.5 6 10 5.5 12 5.5"
            fill="none"
            stroke="#fff"
            strokeWidth={1}
            opacity={0.3}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  'safety-vest': {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M6 4H18L20 22H4L6 4Z" fill={c} />
          <path d="M9 4V10L12 12L15 10V4" fill={c} opacity={0.7} />
          {WL(4, 14, 20, 14, 0.5, 1.5)}
          {WL(4, 18, 20, 18, 0.5, 1.5)}
        </React.Fragment>
      );
    },
  },
  shoe: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M3 14L5 8H13L14 10H21C22 10 22 14 21 14H19L17 18H5L3 14Z" fill={c} />
          {W('M5 8L3 14M13 8L14 10H21', 0.35, 1)}
          <line x1={5} y1={18} x2={17} y2={18} stroke={c} strokeWidth={2} />
        </React.Fragment>
      );
    },
  },

  // ═══ LOGISTIK-FEATURES (Cyan) ═══
  crane: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M6 22V6L18 3V6H22V9H18V22"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <rect x={3} y={20} width={18} height={2} rx={1} fill={c} />
          <path
            d="M18 9V14L15 14L15 18"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round" />
          <rect x={13} y={18} width={4} height={2} fill={c} />
        </React.Fragment>
      );
    },
  },
  barrel: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <ellipse cx={12} cy={4} rx={7} ry={3} fill={c} />
          <path
            d="M5 4V20C5 21.7 8 23 12 23C16 23 19 21.7 19 20V4"
            fill={c}
            opacity={0.85} />
          <ellipse cx={12} cy={4} rx={5} ry={2} fill="#fff" opacity={0.15} />
          {WL(5, 10, 19, 10, 0.2, 1)}
          {WL(5, 16, 19, 16, 0.2, 1)}
        </React.Fragment>
      );
    },
  },
  scale: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <line
            x1={12}
            y1={2}
            x2={12}
            y2={18}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <path d="M4 8L8 16H0Z" fill={c} />
          <path d="M20 8L24 16H16Z" fill={c} />
          <line
            x1={4}
            y1={8}
            x2={20}
            y2={8}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <rect x={8} y={19} width={8} height={3} rx={1} fill={c} />
          {W('M4 8L2 14M20 8L18 14', 0.3, 1)}
        </React.Fragment>
      );
    },
  },
  snowflake: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 2V22M4.9 7L19.1 17M19.1 7L4.9 17"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <path
            d="M12 2L9.5 5M12 2L14.5 5M12 22L9.5 19M12 22L14.5 19M4.9 7L7 5M4.9 7L4.5 10M19.1 7L17 5M19.1 7L19.5 10M4.9 17L7 19M4.9 17L4.5 14M19.1 17L17 19M19.1 17L19.5 14"
            fill="none"
            stroke={c}
            strokeWidth={1.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  thermometer: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M14 14.5V4C14 2.3 12.7 1 11 1C9.3 1 8 2.3 8 4V14.5C6.2 15.8 5 17.8 5 20C5 23.3 7.7 23 11 23C14.3 23 17 23.3 17 20C17 17.8 15.8 15.8 14 14.5Z"
            fill={c} />
          <circle cx={11} cy={19} r={3} fill="#fff" opacity={0.3} />
          <rect x={9.5} y={8} width={3} height={8} rx={1.5} fill="#fff" opacity={0.25} />
        </React.Fragment>
      );
    },
  },
  'ice-cube': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 8L12 3L20 8V16L12 21L4 16Z" fill={c} />
          {W('M12 11L20 8M12 11L4 8M12 11V21', 0.4, 1)}
          <path d="M4 8L12 3L20 8L12 11Z" fill="#fff" opacity={0.15} />
        </React.Fragment>
      );
    },
  },
  biohazard: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <circle cx={12} cy={12} r={2} fill="#fff" opacity={0.4} />
          <circle
            cx={12}
            cy={6}
            r={3}
            fill="none"
            stroke="#fff"
            strokeWidth={1.5}
            opacity={0.4} />
          <circle
            cx={7}
            cy={16}
            r={3}
            fill="none"
            stroke="#fff"
            strokeWidth={1.5}
            opacity={0.4} />
          <circle
            cx={17}
            cy={16}
            r={3}
            fill="none"
            stroke="#fff"
            strokeWidth={1.5}
            opacity={0.4} />
        </React.Fragment>
      );
    },
  },
  radioactive: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          <circle cx={12} cy={12} r={2.5} fill="#fff" opacity={0.4} />
          <path d="M12 3L15 9.5H9Z" fill="#fff" opacity={0.3} />
          <path d="M19 17.5L15.5 11L12.5 14.5Z" fill="#fff" opacity={0.3} />
          <path d="M5 17.5L8.5 11L11.5 14.5Z" fill="#fff" opacity={0.3} />
        </React.Fragment>
      );
    },
  },
  leaf: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M6 21C6 21 3 14 6 8C9 2 18 2 21 3C21 3 22 12 18 16C14 20 6 21 6 21Z"
            fill={c} />
          <path
            d="M6 21C10 16 14 12 21 3"
            fill="none"
            stroke="#fff"
            strokeWidth={1.2}
            opacity={0.4} />
          <path
            d="M10 17C12 14 14 12 17 9"
            fill="none"
            stroke="#fff"
            strokeWidth={0.8}
            opacity={0.25} />
        </React.Fragment>
      );
    },
  },
  apple: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M12 6C8 6 4 9 4 14C4 19 7 22 10 22C11 22 11.5 21.5 12 21.5C12.5 21.5 13 22 14 22C17 22 20 19 20 14C20 9 16 6 12 6Z"
            fill={c} />
          <path d="M12 2C12 2 15 3 14 6H10C10 3 12 2 12 2Z" fill={c} opacity={0.7} />
          <path
            d="M8 10C9 8 10.5 7 12 7"
            fill="none"
            stroke="#fff"
            strokeWidth={1}
            opacity={0.3}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  ruler: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={8} width={20} height={8} rx={1} fill={c} />
          {WL(5, 8, 5, 12, 0.4)}
          {WL(8, 8, 8, 11, 0.4)}
          {WL(11, 8, 11, 12, 0.4)}
          {WL(14, 8, 14, 11, 0.4)}
          {WL(17, 8, 17, 12, 0.4)}
          {WL(20, 8, 20, 11, 0.4)}
        </React.Fragment>
      );
    },
  },
  satellite: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={3} fill={c} />
          <path
            d="M7 7L4 4M17 7L20 4M7 17L4 20M17 17L20 20"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <rect x={2} y={2} width={4} height={4} rx={1} fill={c} opacity={0.7} />
          <rect x={18} y={2} width={4} height={4} rx={1} fill={c} opacity={0.7} />
          <rect x={2} y={18} width={4} height={4} rx={1} fill={c} opacity={0.7} />
          <rect x={18} y={18} width={4} height={4} rx={1} fill={c} opacity={0.7} />
        </React.Fragment>
      );
    },
  },
  mobile: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={6} y={2} width={12} height={20} rx={2} fill={c} />
          <rect x={8} y={4} width={8} height={12} rx={1} fill="#fff" opacity={0.2} />
          {WC(12, 19, 1.2, 0.35)}
        </React.Fragment>
      );
    },
  },

  // ═══ SONSTIGES ═══
  printer: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={8} width={20} height={10} rx={2} fill={c} />
          <rect x={5} y={2} width={14} height={6} rx={1} fill={c} opacity={0.7} />
          <rect x={5} y={16} width={14} height={6} rx={1} fill={c} opacity={0.8} />
          {WL(8, 18, 16, 18, 0.3)}
          {WL(8, 20, 13, 20, 0.3)}
          {WC(17, 12, 1, 0.3)}
        </React.Fragment>
      );
    },
  },
  target: {
    c: C.orange,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} opacity={0.25} />
          <circle cx={12} cy={12} r={7} fill={c} opacity={0.5} />
          <circle cx={12} cy={12} r={4} fill={c} />
          {WC(12, 12, 1.5, 0.6)}
        </React.Fragment>
      );
    },
  },
  chart: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={3} width={18} height={18} rx={2} fill={c} />
          <rect x={6} y={13} width={3} height={5} rx={0.5} fill="#fff" opacity={0.35} />
          <rect x={10.5} y={9} width={3} height={9} rx={0.5} fill="#fff" opacity={0.45} />
          <rect x={15} y={6} width={3} height={12} rx={0.5} fill="#fff" opacity={0.55} />
        </React.Fragment>
      );
    },
  },
  briefcase: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={7} width={20} height={14} rx={2} fill={c} />
          <path
            d="M8 7V5C8 3.9 8.9 3 10 3H14C15.1 3 16 3.9 16 5V7"
            fill="none"
            stroke={c}
            strokeWidth={2} />
          {WL(2, 12, 22, 12, 0.3, 1.2)}
        </React.Fragment>
      );
    },
  },
  lightning: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M13 2L4 14H11L10 22L20 10H13L13 2Z" fill={c} />
          <path d="M11 5L6 12H10L9 18" fill="#fff" opacity={0.2} />
        </React.Fragment>
      );
    },
  },
  cookie: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <circle cx={12} cy={12} r={10} fill={c} />
          {WC(8, 8, 1.5, 0.3)}
          {WC(14, 7, 1, 0.3)}
          {WC(10, 13, 1.2, 0.3)}
          {WC(15, 12, 1.5, 0.3)}
          {WC(7, 16, 1, 0.3)}
          {WC(13, 17, 1.3, 0.3)}
        </React.Fragment>
      );
    },
  },
  pallet: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={14} width={20} height={3} rx={1} fill={c} />
          <rect x={3} y={17} width={3} height={4} fill={c} opacity={0.8} />
          <rect x={10.5} y={17} width={3} height={4} fill={c} opacity={0.8} />
          <rect x={18} y={17} width={3} height={4} fill={c} opacity={0.8} />
          <rect x={4} y={6} width={16} height={8} rx={1} fill={c} opacity={0.5} />
          {WL(4, 10, 20, 10, 0.3)}
        </React.Fragment>
      );
    },
  },
  couch: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M4 10V7C4 5.9 4.9 5 6 5H18C19.1 5 20 5.9 20 7V10"
            fill={c}
            opacity={0.7} />
          <path d="M2 10H22V17C22 18.1 21.1 19 20 19H4C2.9 19 2 18.1 2 17V10Z" fill={c} />
          <rect x={5} y={19} width={3} height={2} rx={1} fill={c} opacity={0.7} />
          <rect x={16} y={19} width={3} height={2} rx={1} fill={c} opacity={0.7} />
          {WL(7, 12, 17, 12, 0.25)}
        </React.Fragment>
      );
    },
  },
  cart: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M5 5H22L19 15H8L5 5Z" fill={c} />
          <path
            d="M1 2H4L5 5"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <circle cx={10} cy={19} r={2} fill={c} />
          <circle cx={18} cy={19} r={2} fill={c} />
          {WL(8, 8, 20, 8, 0.25)}
        </React.Fragment>
      );
    },
  },
  'shopping-bag': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M4 7H20L19 22H5L4 7Z" fill={c} />
          <path
            d="M8 7V5C8 3 9.8 1 12 1C14.2 1 16 3 16 5V7"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          {WL(7, 12, 17, 12, 0.2)}
        </React.Fragment>
      );
    },
  },
  restaurant: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path
            d="M5 2V10C5 11.7 6.3 13 8 13V22"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <line
            x1={5}
            y1={6}
            x2={11}
            y2={6}
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
          <path
            d="M16 2V8C16 10 17 12 19 12V22"
            fill="none"
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
          <line
            x1={19}
            y1={2}
            x2={19}
            y2={8}
            stroke={c}
            strokeWidth={2.5}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  fuel: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={3} y={4} width={12} height={18} rx={2} fill={c} />
          <rect x={5} y={6} width={8} height={6} rx={1} fill="#fff" opacity={0.2} />
          <path
            d="M15 8H17C18.1 8 19 8.9 19 10V16C19 17.1 19.9 18 21 18C22.1 18 21 17 21 16V10"
            fill="none"
            stroke={c}
            strokeWidth={2}
            strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  keyboard: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={6} width={20} height={12} rx={2} fill={c} />
          {WC(6, 10, 1, 0.3)}
          {WC(10, 10, 1, 0.3)}
          {WC(14, 10, 1, 0.3)}
          {WC(18, 10, 1, 0.3)}
          {WL(7, 14, 17, 14, 0.35, 2)}
        </React.Fragment>
      );
    },
  },
  construction: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <rect
            x={1}
            y={10}
            width={22}
            height={4}
            rx={1}
            fill={c}
            transform="rotate(-30 12 12)" />
          <rect
            x={1}
            y={10}
            width={22}
            height={4}
            rx={1}
            fill={c}
            transform="rotate(30 12 12)" />
          <path d="M2 3L6 5L4 9L2 3Z" fill={c} opacity={0.7} />
          <path d="M22 3L18 5L20 9L22 3Z" fill={c} opacity={0.7} />
        </React.Fragment>
      );
    },
  },

  // ═══ NACHTRÄGLICH ERGÄNZTE ICONS ═══════════════════════════
  alert: {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill={c} />
          {WL(12, 9, 12, 13, 0.8, 2)}
          {WC(12, 17, 1, 0.8)}
        </React.Fragment>
      );
    },
  },
  circle: {
    c: C.brand,
    r: function (c) {
      return <circle cx={12} cy={12} r={9} fill={c} />;
    },
  },
  hash: {
    c: C.slate,
    r: function (c) {
      return (
        <React.Fragment>
          {WL(4, 9, 20, 9, 0, 0)}
          {WL(4, 15, 20, 15, 0, 0)}
          {WL(10, 3, 8, 21, 0, 0)}
          {WL(16, 3, 14, 21, 0, 0)}
          <line x1={4} y1={9} x2={20} y2={9} stroke={c} strokeWidth={2} strokeLinecap="round" />
          <line x1={4} y1={15} x2={20} y2={15} stroke={c} strokeWidth={2} strokeLinecap="round" />
          <line x1={10} y1={3} x2={8} y2={21} stroke={c} strokeWidth={2} strokeLinecap="round" />
          <line x1={16} y1={3} x2={14} y2={21} stroke={c} strokeWidth={2} strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  inbox: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M22 12H16L14 15H10L8 12H2" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5.45 5.11L2 12V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V12L18.55 5.11C18.21 4.43 17.52 4 16.76 4H7.24C6.48 4 5.79 4.43 5.45 5.11Z" fill={c} opacity={0.15} stroke={c} strokeWidth={1.5} />
        </React.Fragment>
      );
    },
  },
  'map-pin': {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 5.03 7.03 1 12 1C16.97 1 21 5.03 21 10Z" fill={c} />
          {WC(12, 10, 3, 0.8)}
        </React.Fragment>
      );
    },
  },
  minus: {
    c: C.slate,
    r: function (c) {
      return <line x1={5} y1={12} x2={19} y2={12} stroke={c} strokeWidth={2.5} strokeLinecap="round" />;
    },
  },
  pause: {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={6} y={4} width={4} height={16} rx={1} fill={c} />
          <rect x={14} y={4} width={4} height={16} rx={1} fill={c} />
        </React.Fragment>
      );
    },
  },
  plus: {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <line x1={12} y1={5} x2={12} y2={19} stroke={c} strokeWidth={2.5} strokeLinecap="round" />
          <line x1={5} y1={12} x2={19} y2={12} stroke={c} strokeWidth={2.5} strokeLinecap="round" />
        </React.Fragment>
      );
    },
  },
  'trending-up': {
    c: C.brand,
    r: function (c) {
      return (
        <React.Fragment>
          <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" fill="none" stroke={c} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="17,6 23,6 23,12" fill="none" stroke={c} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  'trending-down': {
    c: C.red,
    r: function (c) {
      return (
        <React.Fragment>
          <polyline points="23,18 13.5,8.5 8.5,13.5 1,6" fill="none" stroke={c} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="17,18 23,18 23,12" fill="none" stroke={c} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
  'admin-email-field': {
    c: C.profile,
    r: function (c) {
      return (
        <React.Fragment>
          <rect x={2} y={4} width={20} height={16} rx={3} fill={c} opacity={0.15} stroke={c} strokeWidth={1.5} />
          <polyline points="2,4 12,13 22,4" fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          {W("M8 12L2 17", 0.4, 1.2)}
          {W("M16 12L22 17", 0.4, 1.2)}
        </React.Fragment>
      );
    },
  },
  'admin-reset-field': {
    c: C.amber,
    r: function (c) {
      return (
        <React.Fragment>
          <path d="M1 4V10H7" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3.51 15A9 9 0 1 0 5.64 5.64L1 10" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </React.Fragment>
      );
    },
  },
};
ICONS.close = ICONS.cross;

/* ═══ MAIN COMPONENT ═══ */

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
  className?: string;
  onClick?: MouseEventHandler<SVGSVGElement>;
}

export default function Icon(props: IconProps): React.JSX.Element | null {
  const name = props.name;
  const size = props.size || 20;
  const color = props.color;
  const style = props.style;
  const className = props.className;
  const onClick = props.onClick;

  const icon = ICONS[name];
  if (!icon) {
    if (typeof console !== 'undefined') console.warn('[Icon] Unknown icon: ' + name);
    return null;
  }

  const c = color || icon.c;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={Object.assign({ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }, style || {})}
      className={className}
      onClick={onClick}>
      {icon.r(c)}
    </svg>
  );
}

/* ═══ AVAILABLE ICON NAMES (for reference) ═══ */
export const ICON_NAMES: string[] = Object.keys(ICONS);
